import { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Badge, gbp, Loading } from "@/components/ui-bits";
import { toast } from "sonner";
import { Home, Plus, X, Loader2, Trash2, MapPin, User, Calendar, TrendingUp, Globe, Check, ExternalLink, Megaphone, Radio } from "lucide-react";
import { usePortalReadiness, PortalBadge, MarketingModal } from "@/components/ListingMarketing";

const SALE_PORTALS = [
  { key: "Rightmove", desc: "UK's largest property portal", color: "bg-[#00DEB6]/15 text-[#0a7a67] border-[#00DEB6]/30", url: (q) => `https://www.rightmove.co.uk/property-for-sale/find.html?searchLocation=${encodeURIComponent(q)}` },
  { key: "Zoopla", desc: "Major listings & valuations portal", color: "bg-[#8046F1]/10 text-[#5b2bb0] border-[#8046F1]/30", url: (q) => `https://www.zoopla.co.uk/for-sale/property/?q=${encodeURIComponent(q)}` },
  { key: "OnTheMarket", desc: "OnTheMarket.com sales portal", color: "bg-rose-50 text-rose-700 border-rose-200", url: (q) => `https://www.onthemarket.com/for-sale/property/${encodeURIComponent(q)}/` },
];

const STATUSES = ["For Sale", "Under Offer", "Sold STC", "Exchanged", "Completed", "Withdrawn"];
const STATUS_STYLE = {
  "For Sale": "bg-blue-50 text-blue-700 ring-blue-200",
  "Under Offer": "bg-amber-50 text-amber-700 ring-amber-200",
  "Sold STC": "bg-violet-50 text-violet-700 ring-violet-200",
  Exchanged: "bg-teal-50 text-teal-700 ring-teal-200",
  Completed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Withdrawn: "bg-slate-100 text-slate-600 ring-slate-200",
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const empty = { property_ref: "", address: "", asking_price: "", buyer_name: "", offer_amount: "", agent: "", target_date: "", notes: "" };

export default function Sales() {
  const [sales, setSales] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("All");
  const [portalFor, setPortalFor] = useState(null);
  const [portalSel, setPortalSel] = useState([]);
  const [portalBusy, setPortalBusy] = useState(false);
  const [marketingFor, setMarketingFor] = useState(null);
  const [zooplaBusy, setZooplaBusy] = useState(null);

  const publishZoopla = async (s) => {
    setZooplaBusy(s.sale_id);
    try {
      const { data } = await api.post(`/zoopla/listings/${s.sale_id}/publish`);
      if (data.result?.ok) {
        toast.success("Published to Zoopla");
        setSales((prev) => prev.map((x) => x.sale_id === s.sale_id ? { ...x, zoopla_status: "published" } : x));
      } else {
        toast.error(`Zoopla error ${data.result?.status_code}: ${JSON.stringify(data.result?.body)?.slice(0, 140)}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not publish to Zoopla"); }
    finally { setZooplaBusy(null); }
  };

  const removeZoopla = async (s) => {
    if (!window.confirm("Remove this listing from Zoopla?")) return;
    setZooplaBusy(s.sale_id);
    try {
      const { data } = await api.post(`/zoopla/listings/${s.sale_id}/remove`);
      if (data.result?.ok) {
        toast.success("Removed from Zoopla");
        setSales((prev) => prev.map((x) => x.sale_id === s.sale_id ? { ...x, zoopla_status: "removed" } : x));
      } else { toast.error(`Zoopla error ${data.result?.status_code}`); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove from Zoopla"); }
    finally { setZooplaBusy(null); }
  };
  const { map: readyMap, refresh: refreshReadiness } = usePortalReadiness("sale", sales);

  const openPortals = (s) => { setPortalFor(s); setPortalSel(s.portals || []); };
  const togglePortal = (k) => setPortalSel((v) => v.includes(k) ? v.filter((x) => x !== k) : [...v, k]);
  const savePortals = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post(`/sales/${portalFor.sale_id}/portals`, { portals: portalSel });
      setSales((prev) => prev.map((o) => o.sale_id === portalFor.sale_id ? { ...o, portals: data.portals } : o));
      toast.success(portalSel.length ? `Published to ${portalSel.join(", ")}` : "Removed from all portals");
      setPortalFor(null);
    } catch { toast.error("Could not publish to portals"); }
    finally { setPortalBusy(false); }
  };

  const load = () => api.get("/sales").then(({ data }) => setSales(data));
  const [properties, setProperties] = useState([]);
  useEffect(() => { load(); api.get("/properties").then(({ data }) => setProperties(data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/sales", { ...form, asking_price: parseFloat(form.asking_price) || 0, offer_amount: parseFloat(form.offer_amount) || 0, target_date: form.target_date || null });
      toast.success("Sale listing created");
      setOpen(false); setForm(empty); load();
    } catch { toast.error("Could not create listing"); }
    finally { setBusy(false); }
  };

  const setStatus = async (s, status) => {
    setSales((prev) => prev.map((o) => o.sale_id === s.sale_id ? { ...o, status } : o));
    await api.patch(`/sales/${s.sale_id}`, { status });
  };

  const remove = async (s) => {
    await api.delete(`/sales/${s.sale_id}`);
    setSales((prev) => prev.filter((o) => o.sale_id !== s.sale_id));
    toast.success("Listing deleted");
  };

  const shown = sales ? (filter === "All" ? sales : sales.filter((o) => o.status === filter)) : [];
  const counts = sales ? STATUSES.reduce((a, s) => ({ ...a, [s]: sales.filter((o) => o.status === s).length }), {}) : {};
  const pipelineValue = sales ? sales.filter((s) => !["Completed", "Withdrawn"].includes(s.status)).reduce((sum, s) => sum + (s.offer_amount || s.asking_price || 0), 0) : 0;

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 fade-up">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-blue-700 mb-2">Sales</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Sales Pipeline</h1>
          <p className="text-slate-500 mt-1 text-sm">Track properties for sale from listing to completion</p>
        </div>
        <div className="flex items-end gap-3">
          <div className="text-right hidden sm:block">
            <p className="text-xs text-slate-400 flex items-center gap-1 justify-end"><TrendingUp size={13} /> Active pipeline</p>
            <p className="font-display text-xl font-bold text-slate-900">{gbp(pipelineValue)}</p>
          </div>
          <button data-testid="new-sale-button" onClick={() => setOpen(true)}
            className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 text-sm font-medium flex items-center gap-2 transition-colors">
            <Plus size={16} /> New Listing
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {["All", ...STATUSES].map((s) => (
          <button key={s} data-testid={`sales-filter-${s}`} onClick={() => setFilter(s)}
            className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {s} {s !== "All" && <span className="opacity-70">({counts[s] || 0})</span>}
          </button>
        ))}
      </div>

      {!sales ? <Loading /> : shown.length === 0 ? (
        <Card className="p-10 text-center text-slate-400" data-testid="sales-empty">No listings {filter !== "All" ? `with status "${filter}"` : "yet"}. Click “New Listing” to add one.</Card>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {shown.map((s) => (
            <Card key={s.sale_id} className="p-5 flex flex-col" data-testid={`sale-${s.sale_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <p className="text-xs font-mono text-slate-400">{s.reference}</p>
                  <p className="font-semibold text-slate-900 leading-snug truncate">{s.address || s.property_ref || "Listing"}</p>
                </div>
                <p className="font-display text-lg font-bold text-slate-900 shrink-0">{gbp(s.asking_price)}</p>
              </div>
              {s.address && s.property_ref && <p className="text-sm text-slate-500 mb-2 flex items-center gap-1.5"><MapPin size={12} /> {s.address}</p>}
              <div className="mb-2"><PortalBadge readiness={readyMap[s.sale_id]} testid={`portal-badge-${s.sale_id}`} /></div>
              <div className="space-y-1.5 text-xs text-slate-500 mb-4">
                {s.buyer_name && <p className="flex items-center gap-1.5"><User size={12} /> {s.buyer_name}{s.offer_amount > 0 ? ` · offer ${gbp(s.offer_amount)}` : ""}</p>}
                {s.agent && <p className="flex items-center gap-1.5"><User size={12} /> Agent: {s.agent}</p>}
                {s.target_date && <p className="flex items-center gap-1.5"><Calendar size={12} /> Target {fmtDate(s.target_date)}</p>}
                {s.notes && <p className="text-slate-400 line-clamp-2">{s.notes}</p>}
              </div>
              {s.portals?.length > 0 && (
                <div className="mb-3" data-testid={`sale-portals-${s.sale_id}`}>
                  <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider mb-1 flex items-center gap-1"><Globe size={11} /> Live — click to view advert</p>
                  <div className="flex flex-wrap gap-1.5">
                    {s.portals.map((pk) => {
                      const meta = SALE_PORTALS.find((x) => x.key === pk);
                      const q = s.address || s.property_ref || "";
                      return (
                        <a key={pk} href={(meta ? meta.url : () => "#")(q)} target="_blank" rel="noreferrer"
                          data-testid={`view-advert-${s.sale_id}-${pk}`} title={`View live advert on ${pk}`}
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 hover:opacity-80 transition-opacity ${meta ? meta.color : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                          {pk} <ExternalLink size={9} />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
              <button data-testid={`publish-portals-${s.sale_id}`} onClick={() => openPortals(s)}
                className="mb-3 w-full border border-blue-200 text-blue-700 hover:bg-blue-50 rounded-lg px-3 py-1.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors">
                <Globe size={13} /> {s.portals?.length ? "Manage portals" : "Send to portals"}
              </button>
              <button data-testid={`marketing-${s.sale_id}`} onClick={() => setMarketingFor(s)}
                className="mb-3 w-full border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg px-3 py-1.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors">
                <Megaphone size={13} /> Portal details
              </button>
              <div className="mb-3 flex items-center gap-2">
                <button data-testid={`zoopla-publish-${s.sale_id}`} onClick={() => publishZoopla(s)} disabled={zooplaBusy === s.sale_id}
                  className="flex-1 border border-indigo-200 text-indigo-700 hover:bg-indigo-50 rounded-lg px-3 py-1.5 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50">
                  {zooplaBusy === s.sale_id ? <Loader2 size={13} className="animate-spin" /> : <Radio size={13} />}
                  {s.zoopla_status === "published" ? "Update on Zoopla" : "Publish to Zoopla"}
                </button>
                {s.zoopla_status === "published" && (
                  <button data-testid={`zoopla-remove-${s.sale_id}`} onClick={() => removeZoopla(s)} disabled={zooplaBusy === s.sale_id}
                    className="border border-red-200 text-red-600 hover:bg-red-50 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-50" title="Remove from Zoopla">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
              <div className="mt-auto flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
                <select data-testid={`sale-status-${s.sale_id}`} value={s.status} onChange={(e) => setStatus(s, e.target.value)}
                  className={`text-xs font-medium rounded-full px-2.5 py-1 ring-1 ring-inset focus:outline-none ${STATUS_STYLE[s.status]}`}>
                  {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
                <button data-testid={`sale-delete-${s.sale_id}`} onClick={() => remove(s)} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">New Sale Listing</h3>
              <button type="button" data-testid="close-sale" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Property</label>
                  <select data-testid="sale-property" value={form.property_ref} onChange={(e) => { const pr = e.target.value; const p = properties.find((x) => x.reference === pr); setForm({ ...form, property_ref: pr, address: p ? p.address : form.address }); }} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                    <option value="">— Select property —</option>
                    {properties.map((p) => <option key={p.property_id} value={p.reference}>{p.address}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Asking price (£)</label>
                  <input data-testid="sale-asking" type="number" min="0" value={form.asking_price} onChange={(e) => setForm({ ...form, asking_price: e.target.value })} placeholder="350000" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                <input data-testid="sale-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Buyer</label>
                  <input data-testid="sale-buyer" value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Offer (£)</label>
                  <input data-testid="sale-offer" type="number" min="0" value={form.offer_amount} onChange={(e) => setForm({ ...form, offer_amount: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Agent</label>
                  <input data-testid="sale-agent" value={form.agent} onChange={(e) => setForm({ ...form, agent: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Target date</label>
                  <input data-testid="sale-target-date" type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea data-testid="sale-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <button type="submit" data-testid="save-sale" disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Home size={16} />} Create Listing
              </button>
            </form>
          </div>
        </div>
      )}
      {portalFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setPortalFor(null)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="portals-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl font-bold text-slate-900">Send to portals</h3>
              <button type="button" data-testid="close-portals" onClick={() => setPortalFor(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">{portalFor.address || portalFor.property_ref} — choose where this listing goes live.</p>
            <div className="space-y-2 mb-5">
              {SALE_PORTALS.map((p) => {
                const on = portalSel.includes(p.key);
                return (
                  <button key={p.key} type="button" data-testid={`portal-toggle-${p.key}`} onClick={() => togglePortal(p.key)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border text-left transition-colors ${on ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center border ${on ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>{on && <Check size={13} className="text-white" />}</span>
                    <span className="flex-1"><span className="block text-sm font-semibold text-slate-800">{p.key}</span><span className="block text-xs text-slate-400">{p.desc}</span></span>
                  </button>
                );
              })}
            </div>
            <button data-testid="save-portals" onClick={savePortals} disabled={portalBusy}
              className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
              {portalBusy ? <Loader2 size={16} className="animate-spin" /> : <Globe size={16} />} {portalSel.length ? `Publish to ${portalSel.length} portal${portalSel.length > 1 ? "s" : ""}` : "Save (remove from all)"}
            </button>
          </div>
        </div>
      )}
      {marketingFor && (
        <MarketingModal kind="sale" listing={marketingFor} onClose={() => setMarketingFor(null)}
          onSaved={() => { load(); refreshReadiness(); }} />
      )}
    </Layout>
  );
}
